import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp, ChevronUp, ChevronDown, Loader2, Filter, CheckCircle, XCircle } from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { Button } from '../components/shared/Button';
import { SubscriptionLockedState } from '../components/shared/SubscriptionLockedState';
import { resultsService, type ScoreHistoryEntry } from '../services/results.service';
import { examService } from '../services/exam.service';
import { subscriptionService } from '../services/subscription.service';

type SortColumn = 'date' | 'section' | 'score' | 'passed';
type SortOrder = 'asc' | 'desc';

interface ExamResult {
  id: string;
  examId: string;
  date: string;
  section: string;
  score: number;
  passed: boolean;
  durationMinutes: number;
}

function mapHistory(entries: ScoreHistoryEntry[]): ExamResult[] {
  const typeToSection: Record<string, string> = {
    reading: 'Lezen',
    listening: 'Luisteren',
    writing: 'Schrijven',
    speaking: 'Spreken',
    knm: 'KNM',
  };
  return entries.map((e) => ({
    id: e.attemptId,
    examId: e.exam?._id ?? '',
    date: e.date,
    section: typeToSection[e.exam?.type] || e.exam?.type || 'Lezen',
    score: Math.round(e.percentage ?? 0),
    passed: e.passed,
    durationMinutes: e.duration ? Math.round(e.duration / 60) : 0,
  }));
}

export const ExamHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [results, setResults] = useState<ExamResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [trialExamIds, setTrialExamIds] = useState<Set<string>>(new Set());
  const [filterSection, setFilterSection] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const history = await resultsService.getScoreHistory();
        setResults(mapHistory(history));
      } catch (error) {
        console.error('Failed to load results:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSubscription = async () => {
      try {
        const subscription = await subscriptionService.getStatus();
        setIsSubscribed(subscription?.status === 'active');
      } catch (error) {
        console.error('Failed to load subscription:', error);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchResults();
    fetchSubscription();
  }, []);

  useEffect(() => {
    if (subscriptionLoading || isSubscribed) return;

    let cancelled = false;
    examService
      .getExams()
      .then((exams) => {
        if (!cancelled) {
          setTrialExamIds(new Set(exams.map((exam) => exam._id)));
        }
      })
      .catch((error) => {
        console.error('Failed to load trial exams:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [subscriptionLoading, isSubscribed]);

  const trialResults = useMemo(
    () => results.filter((result) => trialExamIds.has(result.examId)),
    [results, trialExamIds],
  );

  const filteredAndSorted = useMemo(() => {
    let filtered = results;

    if (filterSection !== 'all') {
      filtered = filtered.filter((r) => r.section === filterSection);
    }

    return [...filtered].sort((a, b) => {
      let aVal: number | string | boolean = a[sortColumn];
      let bVal: number | string | boolean = b[sortColumn];

      if (sortColumn === 'date') {
        aVal = new Date(a.date).getTime();
        bVal = new Date(b.date).getTime();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [results, filterSection, sortColumn, sortOrder]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const stats = useMemo(() => {
    if (results.length === 0) return { total: 0, passed: 0, avgScore: 0, bestSection: 'N/A' };

    const passed = results.filter((r) => r.passed).length;
    const avgScore = Math.round(results.reduce((sum, r) => sum + r.score, 0) / results.length);
    
    const sectionScores: { [key: string]: number[] } = {};
    results.forEach((r) => {
      if (!sectionScores[r.section]) sectionScores[r.section] = [];
      sectionScores[r.section].push(r.score);
    });

    let bestSection = 'N/A';
    let bestAvg = 0;
    Object.entries(sectionScores).forEach(([section, scores]) => {
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      if (avg > bestAvg) {
        bestAvg = avg;
        bestSection = section;
      }
    });

    return { total: results.length, passed, avgScore, bestSection };
  }, [results]);

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return null;
    return sortOrder === 'desc' 
      ? <ChevronDown className="w-4 h-4 inline ml-1" /> 
      : <ChevronUp className="w-4 h-4 inline ml-1" />;
  };

  if (loading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="h-20" />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('examHistory.title')}</h1>
          <p className="text-gray-600">{t('examHistory.subtitle')}</p>
        </div>

        {!isSubscribed ? (
          <div className="space-y-6">
            <SubscriptionLockedState
              titleKey="subscriptionLock.historyTitle"
              descriptionKey="subscriptionLock.historyDescription"
            />

            {trialResults.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t('subscriptionLock.freeAttemptsTitle')}
                  </h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {trialResults.map((result) => (
                    <button
                      key={result.id}
                      className="w-full p-4 hover:bg-blue-50 transition-colors text-left flex items-center justify-between gap-4"
                      onClick={() => navigate(`/results?attemptId=${result.id}`)}
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{result.section}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(result.date).toLocaleDateString('nl-NL', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })} · {result.durationMinutes} {t('examHistory.table.minutes')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-xl font-bold ${result.passed ? 'text-success' : 'text-error'}`}>
                          {result.score}%
                        </p>
                        <p className={`text-xs ${result.passed ? 'text-success' : 'text-error'}`}>
                          {result.passed ? t('results.passed') : t('results.notPassed')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <p className="text-sm text-gray-500 font-medium">{t('examHistory.totalExams')}</p>
            <p className="text-3xl font-bold text-primary mt-1">{stats.total}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <p className="text-sm text-gray-500 font-medium">{t('examHistory.passed')}</p>
            <p className="text-3xl font-bold text-success mt-1">
              {stats.passed}<span className="text-lg text-gray-400">/{stats.total}</span>
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <p className="text-sm text-gray-500 font-medium">{t('examHistory.avgScore')}</p>
            <p className="text-3xl font-bold text-secondary mt-1">{stats.avgScore}%</p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-5">
            <p className="text-sm text-gray-500 font-medium">{t('examHistory.bestSection')}</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">{stats.bestSection}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={filterSection}
              onChange={(e) => setFilterSection(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent bg-white appearance-none cursor-pointer"
            >
              <option value="all">{t('examHistory.allSections')}</option>
              <option value="Lezen">{t('header.sections.reading')}</option>
              <option value="Luisteren">{t('header.sections.listening')}</option>
              <option value="Schrijven">{t('header.sections.writing')}</option>
              <option value="Spreken">{t('header.sections.speaking')}</option>
              <option value="KNM">{t('header.sections.knm')}</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {results.length === 0 ? (
            <div className="p-12 text-center">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600 text-lg mb-2">{t('examHistory.noResults')}</p>
              <p className="text-gray-400 mb-6">{t('header.startExam')}</p>
              <Button variant="primary" onClick={() => navigate('/exam-selection')}>
                {t('header.startExam')}
              </Button>
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('date')}
                      >
                        {t('examHistory.table.date')} <SortIcon column="date" />
                      </th>
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('section')}
                      >
                        {t('examHistory.table.section')} <SortIcon column="section" />
                      </th>
                      <th
                        className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('score')}
                      >
                        {t('examHistory.table.score')} <SortIcon column="score" />
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {t('examHistory.table.duration')}
                      </th>
                      <th
                        className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('passed')}
                      >
                        {t('examHistory.table.status')} <SortIcon column="passed" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredAndSorted.map((result, idx) => (
                      <tr 
                        key={result.id} 
                        className={`hover:bg-blue-50 transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        onClick={() => navigate(`/results?attemptId=${result.id}`)}
                      >
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="font-medium">
                            {new Date(result.date).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {new Date(result.date).toLocaleTimeString('nl-NL', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {result.section}
                        </td>
                        <td className="px-6 py-4 text-sm text-right">
                          <span className={`text-lg font-bold ${result.passed ? 'text-success' : 'text-error'}`}>
                            {result.score}%
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-gray-600">
                          {result.durationMinutes} {t('examHistory.table.minutes')}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {result.passed ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-semibold">
                              <CheckCircle className="w-3.5 h-3.5" />
                              {t('results.passed')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-error/10 text-error text-xs font-semibold">
                              <XCircle className="w-3.5 h-3.5" />
                              {t('results.notPassed')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden divide-y divide-gray-100">
                {filteredAndSorted.map((result) => (
                  <button
                    key={result.id}
                    className="w-full p-4 hover:bg-blue-50 transition-colors text-left"
                    onClick={() => navigate(`/results?attemptId=${result.id}`)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{result.section}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(result.date).toLocaleDateString('nl-NL', {
                            day: 'numeric',
                            month: 'short',
                          })} · {result.durationMinutes} {t('examHistory.table.minutes')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-xl font-bold ${result.passed ? 'text-success' : 'text-error'}`}>
                          {result.score}%
                        </p>
                        {result.passed ? (
                          <span className="text-xs text-success">{t('results.passed')}</span>
                        ) : (
                          <span className="text-xs text-error">{t('results.notPassed')}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {results.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-secondary" />
              {t('examHistory.performanceTrends')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('examHistory.trendsNote')}
            </p>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-600">
                {t('examHistory.statsNote', { count: stats.total, score: stats.avgScore, section: stats.bestSection })}
              </p>
            </div>
          </div>
        )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ExamHistoryPage;
